/**
 * Client-side quiz draft storage.
 *
 * Unsubmitted quiz answers are persisted per (user, module) so a refresh or a
 * quick navigation away doesn't lose progress. Because the device may be
 * shared, drafts MUST be purged on sign-out and whenever the signed-in user
 * changes — otherwise the next learner sees the previous learner's answers.
 */

export const QUIZ_DRAFT_PREFIX = "gg:quiz-draft:";

export const quizDraftKey = (userId: string | undefined, moduleId: string) =>
  `${QUIZ_DRAFT_PREFIX}${userId ?? "anon"}:${moduleId}`;

/**
 * Envelope written to storage. The owner id is embedded (not just part of the
 * key) so a read can hard-verify ownership before any answer reaches the UI —
 * keys can be copied, renamed or left behind by older builds.
 */
type QuizDraftEnvelope = {
  v: 2;
  ownerId: string;
  answers: Record<string, number>;
};

const ownerIdOf = (userId: string | undefined) => userId ?? "anon";

const removeKey = (key: string) => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

/**
 * True when the account this draft belongs to is still the account signed in on
 * this device. Guards against a stale draft flashing into the UI during a token
 * refresh, cold boot or account switch, before the purge effect has run.
 */
export const isDraftOwnerCurrent = (userId: string | undefined): boolean => {
  if (typeof window === "undefined") return false;
  const lastKnown = readLastAuthUserId();
  // No device marker yet (first ever load) — trust the caller's user id.
  if (lastKnown === null) return true;
  return lastKnown === (userId ?? null);
};

export const readQuizDraft = (
  userId: string | undefined,
  moduleId: string,
): Record<string, number> => {
  if (!moduleId || typeof window === "undefined") return {};
  // Device-level mismatch: the signed-in account changed. Never surface drafts.
  if (!isDraftOwnerCurrent(userId)) return {};
  const key = quizDraftKey(userId, moduleId);
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    // Legacy (v1) drafts had no embedded owner — we cannot prove who wrote them,
    // so they are discarded rather than risk showing another learner's answers.
    if ((parsed as QuizDraftEnvelope).v !== 2) {
      removeKey(key);
      return {};
    }
    const envelope = parsed as QuizDraftEnvelope;
    if (envelope.ownerId !== ownerIdOf(userId)) {
      removeKey(key);
      return {};
    }
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(envelope.answers ?? {})) {
      if (typeof v === "number" && Number.isInteger(v) && v >= 0) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
};

export const writeQuizDraft = (
  userId: string | undefined,
  moduleId: string,
  answers: Record<string, number>,
) => {
  if (!moduleId || typeof window === "undefined") return;
  // Refuse to write on behalf of an account that is no longer the active one.
  if (!isDraftOwnerCurrent(userId)) return;
  try {
    const key = quizDraftKey(userId, moduleId);
    if (Object.keys(answers).length === 0) {
      window.localStorage.removeItem(key);
      return;
    }
    const envelope: QuizDraftEnvelope = { v: 2, ownerId: ownerIdOf(userId), answers };
    window.localStorage.setItem(key, JSON.stringify(envelope));
  } catch {
    /* storage unavailable (private mode / quota) — drafts are best-effort */
  }
};

/**
 * Remove stored drafts. Pass `keepUserId` to retain only the drafts belonging
 * to the currently signed-in learner (used when the active account changes).
 * With no argument, every draft on the device is cleared (sign-out).
 */
export const clearQuizDrafts = (keepUserId?: string) => {
  if (typeof window === "undefined") return;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(QUIZ_DRAFT_PREFIX)) continue;
      if (keepUserId && key.startsWith(`${QUIZ_DRAFT_PREFIX}${keepUserId}:`)) continue;
      doomed.push(key);
    }
    doomed.forEach((k) => window.localStorage.removeItem(k));
    // Notify this tab's mounted readers so in-memory answers are dropped too.
    window.dispatchEvent(new Event("gg:quiz-drafts-purged"));
  } catch {
    /* storage unavailable — nothing to purge */
  }
};

/**
 * Device-level record of the last authenticated user id. Persisted so an
 * account change is detected even when it happened in another tab or while the
 * app was closed (a fresh boot starts with no in-memory user id).
 */
export const LAST_USER_KEY = "gg:last-auth-user";

export const readLastAuthUserId = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(LAST_USER_KEY);
  } catch {
    return null;
  }
};

export const writeLastAuthUserId = (userId: string | null) => {
  if (typeof window === "undefined") return;
  try {
    if (userId) window.localStorage.setItem(LAST_USER_KEY, userId);
    else window.localStorage.removeItem(LAST_USER_KEY);
  } catch {
    /* storage unavailable — best effort */
  }
};

/** Fired in the current tab whenever drafts are purged. */
export const QUIZ_DRAFTS_PURGED_EVENT = "gg:quiz-drafts-purged";

/**
 * Subscribe to draft purges from ANY tab. Fires when `clearQuizDrafts` runs
 * locally, and when another tab records a different (or absent) auth user —
 * which is how a sign-out or account switch propagates across tabs.
 * Returns an unsubscribe function.
 */
export const onQuizDraftsPurged = (handler: () => void) => {
  if (typeof window === "undefined") return () => {};
  const local = () => handler();
  const onStorage = (e: StorageEvent) => {
    if (e.key === null) {
      // localStorage.clear() in another tab
      handler();
      return;
    }
    if (e.key === LAST_USER_KEY && e.oldValue !== e.newValue) handler();
    else if (e.key.startsWith(QUIZ_DRAFT_PREFIX) && e.newValue === null) handler();
  };
  window.addEventListener(QUIZ_DRAFTS_PURGED_EVENT, local);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(QUIZ_DRAFTS_PURGED_EVENT, local);
    window.removeEventListener("storage", onStorage);
  };
};
